import React from 'react';
import PropTypes from 'prop-types';

const Pagination = ({
  itemsPerPage,
  totalItems,
  currentPage,
  onPageChange,
  disabled = false,
}) => {
  const safeItemsPerPage = Number(itemsPerPage) || 1;
  const safeTotalItems = Number(totalItems) || 0;
  const totalPages = Math.ceil(safeTotalItems / safeItemsPerPage);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  const activePage = Number(currentPage) || 1;

  const handlePageChange = (pageNumber) => {
    if (disabled || pageNumber === activePage) {
      return;
    }
    onPageChange(pageNumber);
  };

  const handlePreviousPage = () => {
    if (!disabled && activePage > 1) {
      handlePageChange(activePage - 1);
    }
  };

  const handleNextPage = () => {
    if (!disabled && activePage < pages.length) {
      handlePageChange(activePage + 1);
    }
  };

  const renderPageNumbers = () => {
    if (pages.length <= 5) {
      return pages.map((pageNumber) => (
        <li
          key={pageNumber}
          className={`page-item ${activePage === pageNumber ? 'active' : ''}`}
        >
          <button
            type="button"
            className={`page-link ${activePage === pageNumber ? 'page-active' : ''}`}
            disabled={disabled}
            aria-current={activePage === pageNumber ? 'page' : undefined}
            onClick={() => handlePageChange(pageNumber)}
          >
            {pageNumber}
          </button>
        </li>
      ));
    }

    const pagesToShow = [];

    // Always show first page if not in current range
    if (activePage > 3) {
      pagesToShow.push(
        <li key={1} className={`page-item ${activePage === 1 ? 'active' : ''}`}>
          <button
            type="button"
            className={`page-link ${activePage === 1 ? 'page-active' : ''}`}
            disabled={disabled}
            aria-current={activePage === 1 ? 'page' : undefined}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>
        </li>
      );
    }

    // Show ellipsis after first page if needed
    if (activePage > 4) {
      pagesToShow.push(
        <li key="start-ellipsis" className="disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    // Calculate range around current page
    const startPage = Math.max(1, activePage - 1);
    const endPage = Math.min(pages.length, activePage + 1);

    for (let i = startPage; i <= endPage; i++) {
      pagesToShow.push(
        <li key={i} className={`page-item ${activePage === i ? 'active' : ''}`}>
          <button
            type="button"
            className={`page-link ${activePage === i ? 'page-active' : ''}`}
            disabled={disabled}
            aria-current={activePage === i ? 'page' : undefined}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        </li>
      );
    }

    // Show ellipsis before last page if needed
    if (activePage < pages.length - 3) {
      pagesToShow.push(
        <li key="end-ellipsis" className="disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    // Always show last page if not in current range
    if (activePage < pages.length - 2) {
      pagesToShow.push(
        <li key={pages.length} className="page-item ">
          <button
            type="button"
            className={`page-link ${activePage === pages.length ? 'page-active' : ''}`}
            disabled={disabled}
            aria-current={activePage === pages.length ? 'page' : undefined}
            onClick={() => handlePageChange(pages.length)}
          >
            {pages.length}
          </button>
        </li>
      );
    }

    return pagesToShow;
  };

  return (
    <nav
      aria-label="..."
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 10px',
      }}
    >
      <p>
        Showing{' '}
        {safeTotalItems === 0 ? 0 : safeItemsPerPage * (activePage - 1) + 1} to{' '}
        {Math.min(safeItemsPerPage * activePage, safeTotalItems)} of{' '}
        {safeTotalItems} entries
      </p>

      <ul className="pagination">
        <li
          className={`page-item ${activePage === 1 || disabled ? 'disabled' : ''}`}
        >
          <button
            type="button"
            className="page-link"
            onClick={handlePreviousPage}
          >
            Previous
          </button>
        </li>
        {renderPageNumbers()}
        <li
          className={`page-item ${
            activePage === pages.length || disabled ? 'disabled' : ''
          }`}
        >
          <button type="button" className="page-link" onClick={handleNextPage}>
            Next
          </button>
        </li>
      </ul>
    </nav>
  );
};

Pagination.propTypes = {
  itemsPerPage: PropTypes.number,
  totalItems: PropTypes.number,
  currentPage: PropTypes.any,
  onPageChange: PropTypes.func,
  disabled: PropTypes.bool,
};

export default Pagination;
